import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  Phone, Calculator, CalendarCheck, MapPin, Clock, Paintbrush, Wrench,
  Droplets, ArrowRight, BadgeCheck, Flag, Star, Quote,
} from "lucide-react";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import Marquee from "@/components/Marquee";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { FAQ_PREVIEW } from "@/data/faq";
import { FadeUp, MaskedLine, ScaleIn } from "@/components/Reveal";
import { VeteranBadge, USFlag } from "@/components/Logo";
import { PHONE_DISPLAY, PHONE_TEL, PAUL_PHOTO, AREA_GROUPS, SERVICE_AREA_SUMMARY, CATEGORY_META, GOOGLE_REVIEW_URL } from "@/data/site";

const WHY = [
  { icon: Flag, title: "Proudly Veteran-Owned & Operated", text: "U.S. Army discipline, integrity and follow-through on every job." },
  { icon: MapPin, title: "Locally Owned", text: "Paul lives and works right here in Citrus County — your neighbor, not a franchise." },
  { icon: BadgeCheck, title: "Personal Service", text: "You deal directly with Paul from first call to final walkthrough. No runaround." },
  { icon: Calculator, title: "Convenient Instant Estimates", text: "Know your ballpark price in about a minute, right from your phone." },
  { icon: CalendarCheck, title: "Free On-Site Estimates", text: "Every price is confirmed in person before any work begins — no surprises." },
  { icon: Wrench, title: "Multiple Services, One Trusted Contact", text: "Cleaning, painting, flooring, repairs — one call handles your whole list." },
  { icon: Clock, title: "Easy Online Requests", text: "Request your free on-site estimate online in about a minute — Paul contacts you to arrange a time." },
];

const STEPS = [
  { n: "01", title: "Reach Out", text: "Call Paul, request an instant estimate online, or send a quick message — whatever's easiest." },
  { n: "02", title: "Free On-Site Estimate", text: "Paul contacts you to arrange a convenient time, visits your property, and confirms an exact, honest price." },
  { n: "03", title: "Job Done Right", text: "Veteran-grade workmanship, a tidy job site, and a final walkthrough you're happy with." },
];

const SERVICE_BULLETS = {
  "Exterior Cleaning": ["Pressure & soft washing", "House washing", "Driveways, sidewalks & concrete", "Patios, lanais & pool areas", "Gutters & trash bins"],
  "Home Improvements": ["Interior painting", "Exterior painting", "Flooring installation & repairs", "Minor home improvements"],
  "Handyman & Property Services": ["General handyman work", "Small repairs", "Property maintenance", "General property upkeep"],
};

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const photoY = useTransform(scrollYProgress, [0, 1], [0, 70]);
  const badgeY = useTransform(scrollYProgress, [0, 1], [0, -40]);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    api.get("/reviews").then((r) => setReviews((r.data.reviews || []).slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <Seo
        title=""
        description="Veteran-owned pressure washing, painting, flooring & handyman services in Citrus & Hernando County, FL. Free on-site estimates. Call 352-570-8487."
      />

      {/* HERO */}
      <section ref={heroRef} className="hero-grid relative overflow-hidden" data-testid="hero-section">
        <div className="pointer-events-none absolute -right-32 -top-32 h-96 w-96 rounded-full bg-brand-orange/10 blur-3xl" />
        <div className="pointer-events-none absolute -left-24 bottom-0 h-72 w-72 rounded-full bg-brand-blue/10 blur-3xl" />
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 pb-20 pt-12 sm:px-6 lg:grid-cols-[1.15fr_0.85fr] lg:pb-28 lg:pt-16">
          <div>
            <motion.div initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <VeteranBadge />
            </motion.div>
            <h1 className="mt-7 font-heading text-4xl font-extrabold leading-[1.02] tracking-tight text-brand-blue sm:text-6xl lg:text-7xl" data-testid="hero-headline">
              <MaskedLine delay={0.05}>ONE CALL.</MaskedLine>
              <MaskedLine delay={0.12}>PAUL DOES</MaskedLine>
              <MaskedLine delay={0.2}>
                <span className="relative inline-block text-brand-orange">
                  IT ALL!
                  <motion.svg
                    className="absolute -bottom-2 left-0 w-full"
                    viewBox="0 0 200 12"
                    initial={{ pathLength: 0 }}
                    aria-hidden="true"
                  >
                    <motion.path
                      d="M4 9 C 60 2, 140 2, 196 8"
                      fill="none"
                      stroke="#F47B20"
                      strokeWidth="5"
                      strokeLinecap="round"
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: 1 }}
                      transition={{ duration: 0.7, delay: 0.5, ease: "easeOut" }}
                    />
                  </motion.svg>
                </span>
              </MaskedLine>
            </h1>
            <motion.p
              className="mt-5 font-heading text-sm font-bold uppercase tracking-[0.18em] text-brand-charcoal sm:text-base"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.28 }}
              data-testid="hero-services-line"
            >
              <span className="whitespace-nowrap text-brand-orange">Pressure Washing</span>
              <span className="mx-2 text-brand-orange/50">•</span> <span className="whitespace-nowrap">Painting</span>
              <span className="mx-2 text-brand-orange/50">•</span> <span className="whitespace-nowrap">Flooring</span>
              <span className="mx-2 text-brand-orange/50">•</span> <span className="whitespace-nowrap">Handyman Services</span>
              <span className="mx-2 text-brand-orange/50">•</span> <span className="whitespace-nowrap">Property Maintenance</span>
            </motion.p>
            <motion.p
              className="mt-5 max-w-xl text-lg font-medium leading-relaxed text-brand-muted"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.36 }}
              data-testid="hero-subheadline"
            >
              Veteran-Owned Home &amp; Property Services Proudly Serving Citrus County, Hernando County &amp;
              The Villages Area — from pressure washing and painting to flooring, handyman services, and
              property maintenance, Just Call Paul makes taking care of your home easier.
            </motion.p>
            <motion.p
              className="mt-5 flex max-w-xl items-start gap-2 text-sm font-semibold text-brand-blue"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.42 }}
              data-testid="hero-service-area-line"
            >
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand-orange" />
              Serving Inverness, Floral City, Crystal River, Homosassa, Spring Hill, Brooksville, The Villages &amp;
              surrounding communities
            </motion.p>
            <motion.div
              className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.48 }}
            >
              <Link
                to="/estimate"
                className="group flex items-center justify-center gap-2 rounded-full bg-brand-orange px-7 py-4 text-sm font-bold uppercase tracking-wide text-white shadow-xl shadow-brand-orange/30 transition-all duration-200 hover:-translate-y-0.5 hover:bg-brand-orangedark"
                data-testid="hero-estimate-button"
              >
                <Calculator className="h-4 w-4 transition-transform duration-300 group-hover:rotate-12" />
                Get an Instant Estimate
              </Link>
              <Link
                to="/schedule"
                className="flex items-center justify-center gap-2 rounded-full border-2 border-brand-blue px-7 py-4 text-sm font-bold uppercase tracking-wide text-brand-blue transition-colors duration-200 hover:bg-brand-blue hover:text-white"
                data-testid="hero-schedule-button"
              >
                <CalendarCheck className="h-4 w-4" />
                Request a Free On-Site Estimate
              </Link>
              <a
                href={`tel:${PHONE_TEL}`}
                className="flex items-center justify-center gap-2 rounded-full px-4 py-4 text-sm font-bold text-brand-charcoal transition-colors hover:text-brand-orange"
                data-testid="hero-call-button"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-blue text-white">
                  <Phone className="h-4 w-4" />
                </span>
                Call Paul — {PHONE_DISPLAY}
              </a>
            </motion.div>
            <motion.div
              className="mt-9 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs font-semibold text-brand-muted"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.55 }}
              data-testid="hero-trust-row"
            >
              {["Free on-site estimates", "One trusted contact", "Citrus County local"].map((t) => (
                <span key={t} className="flex items-center gap-1.5">
                  <Star className="h-3.5 w-3.5 fill-brand-orange text-brand-orange" /> {t}
                </span>
              ))}
            </motion.div>
          </div>

          <motion.div
            className="relative"
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            <motion.div style={{ y: photoY }} className="relative mx-auto max-w-md">
              <div className="absolute -inset-3 -rotate-2 rounded-[2rem] border-2 border-brand-orange/50" aria-hidden="true" />
              <div className="relative overflow-hidden rounded-[2rem] shadow-2xl shadow-brand-blue/25">
                <img
                  src={PAUL_PHOTO}
                  alt="Paul, owner of Just Call Paul Home & Property Services, in front of a Citrus County home"
                  width={900}
                  height={1200}
                  fetchPriority="high"
                  decoding="async"
                  className="aspect-[3/4] w-full object-cover"
                  data-testid="hero-paul-photo"
                />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-brand-navy/85 to-transparent p-6 pt-16">
                  <p className="font-heading text-xl font-bold text-white">Meet Paul</p>
                  <p className="text-sm text-white/80">Owner · U.S. Army Veteran · Your Citrus County neighbor</p>
                </div>
              </div>
            </motion.div>
            <motion.div style={{ y: badgeY }} className="absolute -left-2 top-8 sm:-left-8">
              <div className="glass animate-float-slow rounded-2xl border border-white/60 p-3.5 shadow-xl" data-testid="hero-badge-veteran">
                <p className="flex items-center gap-2 text-xs font-bold text-brand-blue">
                  <USFlag className="h-3.5 w-5 rounded-[2px]" /> U.S. Army Veteran
                </p>
              </div>
            </motion.div>
            <motion.div style={{ y: badgeY }} className="absolute -right-2 bottom-24 sm:-right-6">
              <div className="glass animate-float-slow rounded-2xl border border-white/60 p-3.5 shadow-xl [animation-delay:1.5s]" data-testid="hero-badge-estimate">
                <p className="text-xs font-bold text-brand-blue">Free On-Site Estimates</p>
                <p className="text-xs font-semibold text-brand-muted">No obligation, ever</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <Marquee
        items={["Pressure Washing", "House Washing", "Painting", "Flooring", "Handyman Services", "Property Maintenance", "Veteran Owned", "Citrus County, FL"]}
      />

      {/* FEATURED: PRESSURE WASHING */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:py-24" data-testid="featured-pressure-washing">
        <div className="grid items-center gap-10 lg:grid-cols-2">
          <ScaleIn>
            <div className="relative">
              <div className="absolute -inset-3 rotate-1 rounded-[2rem] border-2 border-brand-orange/50" aria-hidden="true" />
              <img
                src="/work-before-after.webp"
                alt="Before and after photos of a real Just Call Paul driveway and sidewalk pressure-washing project"
                width={1000}
                height={667}
                loading="lazy"
                decoding="async"
                className="relative aspect-[4/3] w-full rounded-[2rem] object-cover shadow-2xl shadow-brand-blue/20"
                data-testid="featured-pw-photo"
              />
              <span className="absolute left-5 top-5 rounded-full bg-brand-orange px-4 py-2 text-xs font-bold uppercase tracking-wide text-white shadow-lg" data-testid="featured-pw-badge">
                Featured Service
              </span>
              <span className="absolute bottom-5 left-5 rounded-full bg-brand-navy/90 px-4 py-2 text-xs font-bold uppercase tracking-wide text-white" data-testid="featured-pw-real-badge">
                Real Just Call Paul Project
              </span>
            </div>
          </ScaleIn>
          <FadeUp delay={0.1}>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-orange">Brand-New Professional Equipment</p>
            <h2 className="mt-3 font-heading text-2xl font-bold tracking-tight text-brand-blue sm:text-3xl lg:text-4xl">
              Pressure Washing &amp; Exterior Cleaning
            </h2>
            <p className="mt-4 leading-relaxed text-brand-muted">
              Paul has invested in brand-new professional pressure-washing equipment, and it shows. From full house
              washing to driveways that look new again, your home's exterior gets the deepest clean — with the
              right method for every surface.
            </p>
            <ul className="mt-5 grid gap-2.5 text-sm text-brand-charcoal sm:grid-cols-2">
              {["House washing & soft washing", "Driveways, sidewalks & concrete", "Patios, lanais & pool decks", "Gutters & trash-bin cleaning"].map((b) => (
                <li key={b} className="flex items-center gap-2">
                  <Droplets className="h-4 w-4 shrink-0 text-brand-orange" /> {b}
                </li>
              ))}
            </ul>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                to="/estimate?services=house_washing"
                className="flex items-center gap-2 rounded-full bg-brand-orange px-6 py-3.5 text-sm font-bold uppercase tracking-wide text-white shadow-lg shadow-brand-orange/25 transition-all duration-200 hover:-translate-y-0.5 hover:bg-brand-orangedark"
                data-testid="featured-pw-estimate-button"
              >
                <Calculator className="h-4 w-4" /> Get an Instant Estimate
              </Link>
              <Link
                to="/pressure-washing"
                className="flex items-center gap-2 rounded-full border-2 border-brand-blue px-6 py-3.5 text-sm font-bold text-brand-blue transition-colors hover:bg-brand-blue hover:text-white"
                data-testid="featured-pw-learn-more"
              >
                Explore Pressure Washing <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:py-28" data-testid="why-section">
        <FadeUp>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-orange">Why Choose Just Call Paul?</p>
          <h2 className="mt-3 max-w-2xl font-heading text-2xl font-bold tracking-tight text-brand-blue sm:text-3xl lg:text-4xl">
            The neighbor who shows up, does it right, and stands behind the work.
          </h2>
        </FadeUp>
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {WHY.map((item, i) => (
            <FadeUp key={item.title} delay={i * 0.06} className={i === 0 ? "sm:col-span-2 lg:col-span-1" : ""}>
              <div className="group h-full rounded-2xl border border-brand-border bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:border-brand-orange/50 hover:shadow-xl hover:shadow-brand-blue/10" data-testid={`why-card-${i}`}>
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-blue/5 text-brand-blue transition-colors duration-300 group-hover:bg-brand-orange group-hover:text-white">
                  <item.icon className="h-6 w-6" />
                </span>
                <h3 className="mt-5 font-heading text-lg font-bold text-brand-charcoal">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-brand-muted">{item.text}</p>
              </div>
            </FadeUp>
          ))}
          <FadeUp delay={0.42} className="sm:col-span-2">
            <Link
              to="/estimate"
              className="group flex h-full items-center justify-between gap-4 rounded-2xl bg-brand-blue p-7 text-white transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-brand-blue/30"
              data-testid="why-cta-card"
            >
              <div>
                <h3 className="font-heading text-lg font-bold">Ready when you are.</h3>
                <p className="mt-1 text-sm text-white/75">Get a ballpark price online in about a minute.</p>
              </div>
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-brand-orange transition-transform duration-300 group-hover:translate-x-1">
                <ArrowRight className="h-5 w-5" />
              </span>
            </Link>
          </FadeUp>
        </div>
      </section>

      {/* SERVICES */}
      <section className="bg-white py-20 lg:py-28" data-testid="services-preview-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <FadeUp className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-orange">What Paul Does</p>
              <h2 className="mt-3 font-heading text-2xl font-bold tracking-tight text-brand-blue sm:text-3xl lg:text-4xl">
                Much more than pressure washing.
              </h2>
              <p className="mt-3 max-w-xl text-brand-muted">
                Three categories, one trusted pro. Every job starts with a free on-site estimate.
              </p>
            </div>
            <Link to="/services" className="group flex items-center gap-2 text-sm font-bold text-brand-orange" data-testid="services-view-all-link">
              View all services
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </FadeUp>
          <div className="mt-12 grid gap-6 lg:grid-cols-3">
            {Object.entries(CATEGORY_META).map(([cat, meta], i) => (
              <ScaleIn key={cat} delay={i * 0.1}>
                <Link to="/services" className="group block h-full" data-testid={`service-category-${i}`}>
                  <div className="relative h-full overflow-hidden rounded-3xl bg-brand-navy text-white shadow-lg transition-all duration-300 group-hover:-translate-y-1.5 group-hover:shadow-2xl group-hover:shadow-brand-blue/25">
                    <div className="relative h-52 overflow-hidden">
                      <img
                        src={meta.image}
                        alt={`${cat} in Citrus County, FL`}
                        width={900}
                        height={600}
                        loading="lazy"
                        decoding="async"
                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-brand-navy via-brand-navy/30 to-transparent" />
                      <h3 className="absolute bottom-4 left-6 right-6 font-heading text-xl font-bold">{cat}</h3>
                    </div>
                    <div className="p-6">
                      <p className="text-sm text-white/70">{meta.tagline}</p>
                      <ul className="mt-4 space-y-2">
                        {SERVICE_BULLETS[cat].map((b) => (
                          <li key={b} className="flex items-center gap-2.5 text-sm text-white/85">
                            <Droplets className="h-3.5 w-3.5 shrink-0 text-brand-orange" /> {b}
                          </li>
                        ))}
                      </ul>
                      <p className="mt-5 flex items-center gap-2 text-sm font-bold text-brand-orange">
                        Explore <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                      </p>
                    </div>
                  </div>
                </Link>
              </ScaleIn>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="hero-grid py-20 lg:py-28" data-testid="how-it-works-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <FadeUp>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-orange">How It Works</p>
            <h2 className="mt-3 font-heading text-2xl font-bold tracking-tight text-brand-blue sm:text-3xl lg:text-4xl">
              Three steps. Zero hassle.
            </h2>
          </FadeUp>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {STEPS.map((s, i) => (
              <FadeUp key={s.n} delay={i * 0.12}>
                <div className="relative h-full rounded-3xl border border-brand-border bg-white p-8" data-testid={`how-step-${i}`}>
                  <span className="font-heading text-5xl font-extrabold text-brand-orange/20">{s.n}</span>
                  <h3 className="mt-4 font-heading text-xl font-bold text-brand-blue">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-brand-muted">{s.text}</p>
                  {i < 2 && <ArrowRight className="absolute -right-4 top-1/2 hidden h-6 w-6 -translate-y-1/2 text-brand-orange md:block" />}
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ESTIMATE BAND */}
      <section className="relative overflow-hidden bg-brand-orange py-16 lg:py-20" data-testid="estimate-band">
        <Paintbrush className="pointer-events-none absolute -right-10 -top-10 h-56 w-56 rotate-12 text-white/10" aria-hidden="true" />
        <div className="mx-auto flex max-w-7xl flex-col items-start gap-8 px-4 sm:px-6 lg:flex-row lg:items-center lg:justify-between">
          <FadeUp>
            <h2 className="font-heading text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
              Know your ballpark price in about a minute.
            </h2>
            <p className="mt-3 max-w-xl text-white/85">
              Pick your services, answer a few quick questions, and get an instant estimated range — then request
              a free on-site visit so Paul can confirm the exact price.
            </p>
          </FadeUp>
          <FadeUp delay={0.15}>
            <Link
              to="/estimate"
              className="flex items-center gap-2 rounded-full bg-brand-navy px-8 py-4 text-sm font-bold uppercase tracking-wide text-white shadow-xl transition-all duration-200 hover:-translate-y-0.5 hover:bg-brand-blue"
              data-testid="estimate-band-button"
            >
              <Calculator className="h-4 w-4" /> Try the Instant Estimate
            </Link>
          </FadeUp>
        </div>
      </section>

      {/* ABOUT PREVIEW */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:py-28" data-testid="about-preview-section">
        <div className="grid items-center gap-12 lg:grid-cols-[0.85fr_1.15fr]">
          <ScaleIn>
            <div className="relative mx-auto max-w-sm">
              <div className="absolute -inset-3 rotate-2 rounded-[2rem] bg-brand-blue/10" aria-hidden="true" />
              <img
                src={PAUL_PHOTO}
                alt="Paul, U.S. Army veteran and owner of Just Call Paul"
                width={900}
                height={1200}
                loading="lazy"
                decoding="async"
                className="relative aspect-[3/4] w-full rounded-[2rem] object-cover shadow-xl"
                data-testid="about-preview-photo"
              />
              <div className="glass absolute -bottom-5 left-1/2 w-max -translate-x-1/2 rounded-full border border-white/60 px-5 py-2.5 shadow-lg">
                <p className="flex items-center gap-2 text-xs font-bold text-brand-blue">
                  <USFlag className="h-3.5 w-5 rounded-[2px]" /> Veteran-Owned &amp; Operated
                </p>
              </div>
            </div>
          </ScaleIn>
          <FadeUp delay={0.1}>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-orange">About Paul</p>
            <h2 className="mt-3 font-heading text-2xl font-bold tracking-tight text-brand-blue sm:text-3xl lg:text-4xl">
              Army veteran. Craftsman. Your neighbor.
            </h2>
            <p className="mt-5 leading-relaxed text-brand-muted">
              Paul served in the United States Army, and he brings that same commitment to hard work, reliability,
              and attention to detail to every home he touches. When you hire Just Call Paul, you get Paul —
              personal attention, honest pricing, and pride in quality workmanship on every job, big or small.
            </p>
            <p className="mt-4 leading-relaxed text-brand-muted">
              He's dedicated to serving homeowners across Citrus County — because this isn't just where he works,
              it's home.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/about" className="rounded-full bg-brand-blue px-6 py-3 text-sm font-bold text-white transition-all duration-200 hover:-translate-y-0.5 hover:bg-brand-deepblue" data-testid="about-preview-button">
                Read Paul's Story
              </Link>
              <a href={`tel:${PHONE_TEL}`} className="flex items-center gap-2 rounded-full border-2 border-brand-blue px-6 py-3 text-sm font-bold text-brand-blue transition-colors hover:bg-brand-blue hover:text-white" data-testid="about-preview-call">
                <Phone className="h-4 w-4" /> {PHONE_DISPLAY}
              </a>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* AREAS */}
      <section className="bg-brand-navy py-20 text-white lg:py-28" data-testid="areas-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <FadeUp>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-orange">Service Areas</p>
            <h2 className="mt-3 max-w-3xl font-heading text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
              Proudly serving Citrus County, Hernando County &amp; The Villages area
            </h2>
          </FadeUp>
          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {AREA_GROUPS.map((group, gi) => (
              <FadeUp key={group.region} delay={gi * 0.08}>
                <div className="h-full rounded-3xl border border-white/15 bg-white/5 p-6" data-testid={`home-region-${gi}`}>
                  <h3 className="flex items-center gap-2 font-heading text-lg font-bold text-brand-orange">
                    <MapPin className="h-5 w-5" /> {group.region}
                  </h3>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {group.areas.map((area) => (
                      <Link
                        key={area}
                        to="/service-areas"
                        className="rounded-full border border-white/20 px-3.5 py-1.5 text-xs font-semibold transition-colors duration-200 hover:border-brand-orange hover:text-brand-orange"
                        data-testid={`area-chip-${area.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        {area}
                      </Link>
                    ))}
                  </div>
                  <p className="mt-4 text-xs leading-relaxed text-white/60">{group.note}</p>
                </div>
              </FadeUp>
            ))}
          </div>
          <FadeUp delay={0.2}>
            <Link to="/service-areas" className="group mt-8 inline-flex items-center gap-2 text-sm font-bold text-brand-orange" data-testid="areas-view-all-link">
              See all areas we serve
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </FadeUp>
        </div>
      </section>

      {/* REVIEWS PREVIEW */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:py-28" data-testid="reviews-preview-section">
        {reviews.length === 0 ? (
          <FadeUp className="mx-auto max-w-2xl text-center">
            <Quote className="mx-auto h-10 w-10 text-brand-orange/40" />
            <h2 className="mt-4 font-heading text-2xl font-bold tracking-tight text-brand-blue sm:text-3xl">
              Real reviews from real neighbors — coming soon.
            </h2>
            <p className="mt-4 text-brand-muted">
              Just Call Paul is a new local business, and we only publish genuine customer reviews — never made-up
              testimonials. Be one of the first to experience the difference, and tell your neighbors about it.
            </p>
            <a href={GOOGLE_REVIEW_URL} target="_blank" rel="noopener noreferrer" className="mt-7 inline-flex items-center gap-2 rounded-full bg-brand-orange px-7 py-3.5 text-sm font-bold uppercase tracking-wide text-white shadow-xl shadow-brand-orange/30 transition-all duration-200 hover:-translate-y-0.5 hover:bg-brand-orangedark" data-testid="home-google-review-button">
              Leave a Google Review <ArrowRight className="h-4 w-4" />
            </a>
          </FadeUp>
        ) : (
          <>
            <FadeUp className="mx-auto max-w-2xl text-center">
              <div className="flex justify-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-6 w-6 fill-brand-orange text-brand-orange" />
                ))}
              </div>
              <h2 className="mt-4 font-heading text-2xl font-bold tracking-tight text-brand-blue sm:text-3xl" data-testid="home-reviews-heading">
                What your neighbors are saying.
              </h2>
              <p className="mt-3 text-brand-muted">
                Genuine reviews from real customers — every one verified by Paul before it's published.
              </p>
            </FadeUp>
            <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="home-reviews-grid">
              {reviews.map((r, i) => (
                <ScaleIn key={r.id} delay={i * 0.06}>
                  <figure className="flex h-full flex-col rounded-2xl border border-brand-border bg-white p-7 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl" data-testid={`home-review-card-${r.id}`}>
                    <span className="flex gap-0.5" aria-label={`${r.rating} out of 5 stars`}>
                      {[...Array(5)].map((_, s) => (
                        <Star key={s} className={`h-4 w-4 ${s < r.rating ? "fill-brand-orange text-brand-orange" : "text-brand-border"}`} />
                      ))}
                    </span>
                    <blockquote className="mt-4 flex-1 text-sm leading-relaxed text-brand-charcoal">"{r.text}"</blockquote>
                    <figcaption className="mt-5 flex items-center gap-2 border-t border-brand-border pt-4">
                      <BadgeCheck className="h-4 w-4 shrink-0 text-brand-blue" />
                      <span className="text-sm font-bold text-brand-charcoal">{r.name}</span>
                      {r.location && <span className="text-xs text-brand-muted">· {r.location}</span>}
                    </figcaption>
                    {r.service && <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-brand-orange">{r.service}</p>}
                  </figure>
                </ScaleIn>
              ))}
            </div>
            <FadeUp className="mt-8 text-center">
              <div className="flex flex-wrap items-center justify-center gap-3">
                <Link to="/reviews" className="inline-flex items-center gap-2 rounded-full border-2 border-brand-blue px-6 py-3 text-sm font-bold text-brand-blue transition-colors hover:bg-brand-blue hover:text-white" data-testid="reviews-preview-link">
                  Read All Reviews <ArrowRight className="h-4 w-4" />
                </Link>
                <a href={GOOGLE_REVIEW_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full bg-brand-orange px-6 py-3 text-sm font-bold uppercase tracking-wide text-white shadow-lg shadow-brand-orange/25 transition-all duration-200 hover:-translate-y-0.5 hover:bg-brand-orangedark" data-testid="home-google-review-button">
                  Leave a Google Review <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            </FadeUp>
          </>
        )}
      </section>

      {/* FAQ PREVIEW */}
      <section className="hero-grid py-20 lg:py-24" data-testid="faq-preview-section">
        <div className="mx-auto max-w-4xl px-4 sm:px-6">
          <FadeUp className="text-center">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-orange">Common Questions</p>
            <h2 className="mt-3 font-heading text-2xl font-bold tracking-tight text-brand-blue sm:text-3xl lg:text-4xl" data-testid="faq-preview-heading">
              Good questions. Straight answers.
            </h2>
          </FadeUp>
          <FadeUp delay={0.1}>
            <Accordion
              type="single"
              collapsible
              className="mt-10 rounded-3xl border border-brand-border bg-white px-6 text-left shadow-sm"
            >
              {FAQ_PREVIEW.map((item, i) => (
                <AccordionItem
                  key={item.q}
                  value={`preview-${i}`}
                  className="border-brand-border last:border-b-0"
                  data-testid={`home-faq-item-${i}`}
                >
                  <AccordionTrigger className="py-5 font-heading text-base font-bold text-brand-charcoal transition-colors hover:text-brand-orange hover:no-underline [&>svg]:text-brand-orange">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-brand-muted">
                    <p>{item.a}</p>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </FadeUp>
          <FadeUp delay={0.15} className="mt-7 text-center">
            <Link to="/faq" className="group inline-flex items-center gap-2 text-sm font-bold text-brand-orange" data-testid="faq-preview-link">
              View all frequently asked questions
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </FadeUp>
        </div>
      </section>


      {/* FINAL CTA SPLIT */}
      <section className="grid md:grid-cols-2" data-testid="final-cta-section">
        <a href={`tel:${PHONE_TEL}`} className="group flex flex-col justify-center bg-brand-blue px-8 py-16 text-white transition-colors duration-300 hover:bg-brand-deepblue sm:px-12" data-testid="final-cta-call">
          <Phone className="h-9 w-9 text-brand-orange" />
          <h2 className="mt-5 font-heading text-2xl font-bold sm:text-3xl">Talk to Paul right now</h2>
          <p className="mt-2 text-white/75">Questions, quotes, or just advice — one call does it all.</p>
          <p className="mt-5 flex items-center gap-2 font-heading text-xl font-extrabold text-brand-orange">
            {PHONE_DISPLAY}
            <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1.5" />
          </p>
        </a>
        <Link to="/schedule" className="group flex flex-col justify-center bg-brand-orange px-8 py-16 text-white transition-colors duration-300 hover:bg-brand-orangedark sm:px-12" data-testid="final-cta-schedule">
          <CalendarCheck className="h-9 w-9" />
          <h2 className="mt-5 font-heading text-2xl font-bold sm:text-3xl">Request your free on-site estimate</h2>
          <p className="mt-2 text-white/85">Send a quick request — Paul contacts you to arrange a convenient time.</p>
          <p className="mt-5 flex items-center gap-2 font-heading text-xl font-extrabold">
            Request Online
            <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1.5" />
          </p>
        </Link>
      </section>
    </div>
  );
}
